import { Tabs, type TabsItem } from "@cynthai/ui";
import { Outlet, useParams, createFileRoute } from "@tanstack/react-router";

import { useCurrentUser } from "@/core/hooks/use-current-user";
import { useTypedRouteTabs } from "@/core/hooks/use-typed-route-tabs";
import { isOrgAdmin, isSuperuser } from "@/core/lib/access";
import PageContainer from "@/core/ui/layout/PageContainer";
import PageHeader from "@/core/ui/layout/PageHeader";
import { useOrganizationDepartments } from "@/features/organization/hooks/use-organization-departments";
import { m } from "@/paraglide/messages";

export const Route = createFileRoute(
  "/_protected/organization/$orgId_/department/$departmentId",
)({
  component: RouteComponent,
});

type DepartmentTabKey = "overview" | "members" | "review";

const TAB_ROUTE_MAP = {
  "/_protected/organization/$orgId_/department/$departmentId/members":
    "members",
  "/_protected/organization/$orgId_/department/$departmentId/review": "review",
  "/_protected/organization/$orgId_/department/$departmentId/": "overview",
} as const;

function RouteComponent() {
  const { orgId, departmentId } = useParams({
    from: "/_protected/organization/$orgId_/department/$departmentId",
  });

  const { data: departments } = useOrganizationDepartments({ id: orgId });
  const department = departments?.find((dept) => dept.id === departmentId);

  const currentUser = useCurrentUser();
  const canReviewChats = isOrgAdmin(currentUser) || isSuperuser(currentUser);

  const selectedKey = useTypedRouteTabs<DepartmentTabKey>({
    tabs: TAB_ROUTE_MAP,
    fallback: "overview",
  });

  const reviewTab: TabsItem<DepartmentTabKey> = {
    label: m.usage_chat_review(),
    value: "review",
    href: {
      to: "/organization/$orgId/department/$departmentId/review",
      params: { orgId, departmentId },
    },
  };

  const tabs: TabsItem<DepartmentTabKey>[] = [
    {
      label: m.department_overview_tab(),
      value: "overview" as const,
      href: {
        to: "/organization/$orgId/department/$departmentId",
        params: { orgId, departmentId },
      },
    },
    {
      label: m.department_members_tab(),
      value: "members" as const,
      href: {
        to: "/organization/$orgId/department/$departmentId/members",
        params: { orgId, departmentId },
      },
    },
    ...(canReviewChats ? [reviewTab] : []),
  ];

  return (
    <>
      <PageHeader title={department?.name ?? m.department_fallback_title()} />
      <PageContainer>
        <Tabs
          items={tabs}
          selectedKey={selectedKey}
          classNames={{ panel: "flex-1 min-h-0 flex flex-col" }}
        />
        <Outlet />
      </PageContainer>
    </>
  );
}
